// Watercolor-inspired palette for light theme
// These colors are desaturated and soft, mimicking watercolor transparency
export const lightFolderColorOptions = [
  '#E8B4CB', // Soft Rose - gentle pink with gray undertones
  '#D4A5D8', // Lavender Mist - muted purple
  '#B8C5E8', // Periwinkle - soft blue-purple
  '#A8D0E6', // Powder Blue - gentle sky blue
  '#9FD3C7', // Seafoam - soft mint green
  '#B8E6B8', // Sage Green - muted natural green
  '#F0E68C', // Khaki - warm yellow-green
  '#F5DEB3', // Wheat - soft golden beige
  '#DDA0DD', // Plum - gentle purple-pink
  '#F0B27A', // Peach - warm orange-pink
  '#AED6F1', // Alice Blue - very soft blue
  '#A9DFBF', // Light Sea Green - gentle aqua
  '#F9E79F', // Banana Yellow - soft warm yellow
  '#FADBD8', // Misty Rose - very light pink
  '#D5DBDB', // Light Gray - neutral watercolor
  '#E8DAEF', // Thistle - soft lavender
];

// Deeper watercolor palette for dark theme
// Richer tones that maintain watercolor softness but show well on dark backgrounds
export const darkFolderColorOptions = [
  '#C2185B', // Deep Rose - rich pink
  '#8E24AA', // Deep Purple - vibrant purple
  '#5E35B1', // Deep Purple Blue
  '#1976D2', // Deep Blue - classic blue
  '#0097A7', // Deep Cyan - teal blue
  '#00796B', // Deep Teal - forest green-blue
  '#689F38', // Deep Lime Green
  '#F57F17', // Deep Amber - golden yellow
  '#FF6F00', // Deep Orange
  '#E64A19', // Deep Orange Red
  '#5D4037', // Deep Brown - earthy
  '#455A64', // Blue Gray - sophisticated
  '#7B1FA2', // Deep Magenta
  '#303F9F', // Deep Indigo
  '#1565C0', // Deep Blue
  '#00838F', // Deep Cyan Teal
];

// Color reference type
export type FolderColor = {
  type: 'reference';
  index: number;
} | {
  type: 'custom';
  hex: string;
};

// Get actual color value based on theme and color reference
export const resolveFolderColor = (color: FolderColor | string, theme: 'light' | 'dark'): string => {
  // Backward compatibility: if it's a string, treat as custom hex
  if (typeof color === 'string') {
    return color;
  }

  if (color.type === 'custom') {
    return color.hex;
  }

  // Reference color - resolve based on theme
  const palette = theme === 'light' ? lightFolderColorOptions : darkFolderColorOptions;
  return palette[color.index] ?? palette[0] ?? '#000000';
};

// Get color options for theme
export const getFolderColorOptions = (theme: 'light' | 'dark') => {
  return theme === 'light' ? lightFolderColorOptions : darkFolderColorOptions;
};

// Create reference color
export const createColorReference = (index: number): FolderColor => ({
  type: 'reference',
  index
});

// Create custom color
export const createCustomColor = (hex: string): FolderColor => ({
  type: 'custom',
  hex
});

// Keep backward compatibility
export const folderColorOptions = darkFolderColorOptions;